import api from '@/api'
import vuex from '@/store'
import store from 'store'
import cookies from 'js-cookie'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import router from '@/router'
import _ from 'lodash'

dayjs.extend(relativeTime)

export default {
  state: {
    list: [],
    groups: [],
    groupMemberships: [],
    current: store.get('ews_current_station', {}),
    settings: {},
    containers: [],
    preferences: {},
    notificationEnabledStationsList: []
  },
  mutations: {
    LOAD_STATIONS(state, list) {
      state.list = list.map((station) => ({
        ...station,
        label: `${station.station.toUpperCase()} - ${station.station_location}`, // add station's location
        value: station.station
      }))
    },
    SET_STATION(state, station) {
      state.current = station
      store.set('ews_current_station', station)
    },
    SET_STATION_PREFERENCES(state, preferences) {
      state.preferences = preferences
    },
    LOAD_STATION_SETTINGS(state, settings) {
      state.settings = settings
    },
    LOAD_CONTAINERS(state, containers) {
      state.containers = containers
    },
    LOAD_GROUPS(state, list) {
      state.groups = list
    },
    LOAD_GROUP_MEMBERSHIPS(state, list) {
      state.groupMemberships = list
    },
    LOAD_NOTIFICATION_ENABLED_STATIONS(state, list) {
      state.notificationEnabledStationsList = list
    }
  },
  actions: {
    async loadStations({ commit, dispatch }) {
      try {
        const response = await api.newsdesk.getStations()
        commit('LOAD_STATIONS', response?.data ?? [])
      } catch (error) {
        throw new Error(`API.newsdesk.getStations() ${error}`)
      }
    },
    async loadStationConfigurations({ commit, dispatch }) {
      const response = await api.newsdesk.getStationConfigurations()
      commit('LOAD_CONFIGS', response?.data ?? [])
    },
    async loadStationContainers({ commit, dispatch }) {
      const response = await api.docker.getStationContainers()
      commit('LOAD_CONTAINERS', response?.data ?? [])
    },
    async loadSettings({ commit }) {
      try {
        const response = await api.newsdesk.getStationSettings()
        commit('LOAD_STATION_SETTINGS', response?.data ?? {})
      } catch (error) {
        console.error('Could not load station settings', error)
      }
    },
    async setStationByName({ state, dispatch, commit }, name) {
      let station = state.list?.find((data) => data.station == name.toLowerCase())
      commit('LOAD_STORIES', [])
      commit('LOAD_RUNDOWN_ITEMS', [])
      commit('LOAD_ASSETS', [])
      commit('LOAD_ARCHIVES', [])
      commit('LOAD_MY_TASKS', [])
      commit('LOAD_MONITORED_STORIES', [])
      commit('LOAD_CALENDAR_STORIES', [])
      commit('CLEAR_ALL_OPTIONS')
      commit('SET_LAST_UPDATED_AT', 0)
      commit('SET_MEDIA_LAST_UPDATED_AT', 0)
      commit('LOAD_STAGEHAND_INFO', {})
      if (station) {
        await dispatch('setStation', station)
      } else throw new Error(`Station ${name} not found`)
    },
    async setDefaultStation({ state, dispatch }, list) {
      let station = {}
      list = list ?? state.list

      if (!list) return console.error('No stations found')

      // set default station
      if (state.current.station && list.length > 0) {
        station = list.find((data) => data.station == state.current.station)
      }
      // try from cookies
      else if (cookies.get('currentStation') && list.length > 0) {
        station = list.find((data) => data.station == cookies.get('currentStation')) ?? station
      }

      // we dont have a current station, select the first station in the list
      if (!station.station && list.length > 0) {
        station = list[0]
      }

      return dispatch('setStation', station)
    },
    async setStation({ commit, state, dispatch }, station) {
      commit('SET_STATION', station)
      if (!station.station) return
      cookies.set('currentStation', station.station, { expires: 365, secure: true, sameSite: 'strict' })

      // if we are switching stations, update the url query param
      if (station.station !== router.history.current.query.station) {
        let query = { ...router.history.current.query, station: station.station }
        router.push({ query }).catch(console.debug)
      }

      dispatch('loadStationPreferences').catch(console.error)
      dispatch('loadStagehandInfo').catch(console.error)

      return true
    },
    async loadStationPreferences({ commit }) {
      // commit('SET_STATION_PREFERENCES', [])
      let resp = await api.preferences.getStationPreferences()

      if (resp?.data) commit('SET_STATION_PREFERENCES', resp.data)
    },
    async saveStationPreferences({ dispatch }, payload) {
      // workaround for bug https://gitlab.scrippsnet.app/biztech/newsdesk/-/issues/772
      if (Array.isArray(payload.data) && payload.data.length == 1) payload.data.push({})
      let resp = await api.preferences.updateStationPreferences(payload.key, payload.data)
      await dispatch('loadStationPreferences')
    },
    async createStationPreferences({ dispatch }, payload) {
      let resp = await api.preferences.createStationPreferences(payload)
      await dispatch('loadStationPreferences')
    },
    async deleteStationPreferences({ dispatch }, key) {
      let resp = await api.preferences.deleteStationPreferences(key)
      await dispatch('loadStationPreferences')
    },
    async loadGroups({ commit, dispatch }) {
      try {
        // const gResp = await api.newsdesk.getAllGroups()
        // get the workspace groups from the access store and pluck the id/name from the stations
        let workspaceGroups =
          vuex.getters.access?.workspaceGroups?.map((g) => {
            return {
              ...g,
              stations: g.stations?.map((s) => {
                return { id: s.id, name: s.station, location: s.station_location }
              }),
              roles: g.roles?.map((r) => r.okta_role)
            }
          }) ?? []

        // if user has direct access to stations, get the dashboards they should have access to
        if (vuex.getters.access?.stations) {
          try {
            let groups = (await getStationGroups(vuex.getters.access.stations)) ?? []
            // add to workspace groups
            workspaceGroups = workspaceGroups.concat(groups)
          } catch (error) {}
        }
        // make workspaceGroups unique by id
        workspaceGroups = _.uniqBy(workspaceGroups, 'id')

        commit('LOAD_GROUPS', workspaceGroups)
      } catch (error) {
        throw new Error(`API.newsdesk.loadGroups() ${error}`)
      }
    },
    async loadNotificationEnabledStations({ commit, dispatch }) {
      try {
        const response = await api.newsdesk.getApiNotificationEnabledStations()
        commit('LOAD_NOTIFICATION_ENABLED_STATIONS', response?.data ?? [])
      } catch (error) {
        console.warn('Error loadNotificationEnabledStations: ', error)
      }
    },
    async setNotificationEnabledStationsList({ commit }, data) {
      try {
        commit('LOAD_NOTIFICATION_ENABLED_STATIONS', data)
      } catch (error) {
        throw new Error(`API.newsdesk.setNotificationEnabledStations() ${error}`)
      }
    }
  },
  getters: {
    currentContainer(state) {
      return state.containers?.find((data) => data.station == state.current.station) ?? {}
    },
    isStagehandRunning(state) {
      let container = state.containers?.find((data) => data.station == state.current.station)
      return container?.task?.Status?.State == 'running'
    },
    currentStation(state) {
      return state.current
    },
    // get current timezone from the current station fall back to user's timezone
    currentTimezone(state) {
      return state.current?.settings?.tz ?? dayjs.tz.guess()
    },
    stationPreferences(state) {
      return state.preferences
    },
    stationSettings(state) {
      return state.current.stagehand ?? {}
    },
    stationContainers(state) {
      return state.containers ?? []
    },
    stationConfigs(state) {
      return state.list ?? []
    },
    stationList(state) {
      return state.list
    },
    workspaceGroups(state) {
      return state.groups
    },
    hasEnterpriseFeature(state, getters, rootState) {
      return true
    },
    isStandAlone(state, getters, rootState) {
      return getters.currentStation.settings?.backend == 'newsdesk' ? true : false
    },
    notificationEnabledStationsList(state) {
      return state.notificationEnabledStationsList
    }
  }
}

const getStationGroups = async (stations) => {
  // get the group memberships for the stations the user has access to
  const mResp = await api.newsdesk.getGroupsByStations(vuex.getters.access.stations)
  const stationsWithGroups = mResp?.data?.results ?? []

  // extract all the groups
  let groups = []
  stationsWithGroups.forEach((s) => {
    groups = groups.concat(s.groups)
  })

  // remove the dupes
  groups = _.uniqBy(groups, 'id')

  // add the station attribute to each group
  groups.forEach((g) => {
    g.stations = stationsWithGroups
      .filter((s) => {
        return s.groups.some((sg) => sg.id == g.id)
      })
      .map((s) => ({ id: s.id, name: s.station }))
  })
  return groups
}
